import React from 'react';
import { motion } from 'framer-motion';
import { ITransacao as Transacao } from '../../types/firestore.types';
import { formatarDataHora } from '../../utils/dateUtils';
import { formatarMoeda } from '../../utils/currencyUtils';
import Card from '../ui/Card';
import Badge from '../ui/Badge';

interface TransactionHistoryListProps {
  transacoes: Transacao[];
}

// Mapeamento de status para cores e rótulos
const STATUS_CONFIG = {
  'pending': { label: 'Pendente', color: 'bg-yellow-500', descricao: 'Aguardando processamento' },
  'validating': { label: 'Em Validação', color: 'bg-blue-500', descricao: 'Em análise do supervisor' },
  'completed': { label: 'Concluído', color: 'bg-green-500', descricao: 'Transação finalizada com sucesso' },
  'failed': { label: 'Falhou', color: 'bg-red-500', descricao: 'Transação não aprovada' },
  'cancelled': { label: 'Cancelado', color: 'bg-gray-500', descricao: 'Transação cancelada pelo usuário' },
  'reversed': { label: 'Estornado', color: 'bg-purple-500', descricao: 'Valor foi estornado' },
  'processing': { label: 'Processando', color: 'bg-orange-500', descricao: 'Em processamento pelo sistema' },
  'refunded': { label: 'Reembolsado', color: 'bg-indigo-500', descricao: 'Valor foi reembolsado' },
  'expired': { label: 'Expirado', color: 'bg-gray-600', descricao: 'Transação expirada' }
} as const;

const getStatusBadge = (status: string, color) => {
  const config = STATUS_CONFIG[status] as any;

  if(config){
  return (
    <Badge
      className={`font-medium ${config.color} text-xs px-2 py-0.5 rounded-full`}
    >
      {config.label}
    </Badge>
  );
  }
};

const TransactionHistoryList: React.FC<TransactionHistoryListProps> = ({ transacoes }) => {
  if (!transacoes || transacoes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-background rounded-lg shadow-lg text-text min-h-[200px]">
        <svg
          className="w-16 h-16 text-secondary mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          ></path>
        </svg>
        <p className="text-lg font-semibold text-secondary text-center">Nenhuma transação encontrada.</p>
        <p className="text-sm text-text-light mt-2">Seu histórico de transações está vazio no momento.</p>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <motion.div
      className="space-y-4 p-4 md:p-6 bg-background rounded-lg shadow-xl max-w-full mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <h2 className="text-2xl font-bold text-primary mb-6 text-center md:text-left">Histórico de Transações</h2>
      <div className="grid grid-cols-1 gap-4">
        {transacoes
          .sort((a, b) => b.data_hora.toMillis() - a.data_hora.toMillis())
          .map((transacao) => (
            <motion.div key={transacao.id} variants={itemVariants}>
              <Card className="p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-background-light hover:bg-background-dark transition-colors duration-200 ease-in-out cursor-pointer border border-primary/40 rounded-lg shadow-md">
                <div className="flex-grow">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge
                      variant={transacao.tipo_operacao === 'credito' ? 'success' : 'danger'}
                      className="font-medium text-xs px-2 py-0.5 rounded-full"
                    >
                      {transacao.tipo_operacao.toUpperCase()}
                    </Badge>
                    {transacao.status && getStatusBadge(transacao.status)}
                    <span className="text-sm text-text-light">
                      {formatarDataHora(transacao.data_hora.toDate())}
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-text">
                    {formatarMoeda(transacao.valor)}
                  </p>
                  <div className="text-sm text-text-light mt-1 space-y-0.5">
                    {transacao.unidade_id && (
                      <p>Unidade: <span className="font-medium text-secondary">{transacao.unidade_id}</span></p>
                    )}
                    {transacao.numero_comanda && (
                      <p>Comanda: <span className="font-medium text-secondary">{transacao.numero_comanda}</span></p>
                    )}
                    {transacao.campanha_id && (
                      <p>Campanha: <span className="font-medium text-accent">{transacao.campanha_id}</span></p>
                    )}
                    {transacao.operador_id && (
                       <p>Operador: <span className="font-medium text-text-light">{transacao.operador_id}</span></p>
                    )}
                  </div>
                </div>
              
              </Card>
            </motion.div>
          ))}
      </div>
    </motion.div>
  );
};

export default TransactionHistoryList;